import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/features/shared/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { CategoryGroup } from '../types';
import { CATEGORY_META } from '../lib/format';

/** A collapsible, metadata-driven category — label/colour/sign come from the
 *  taxonomy; the deduction names come entirely from the data. Used by Details
 *  and YTD. Requires a TooltipProvider ancestor. */
export const CategorySection: React.FC<{ group: CategoryGroup; money: (n: number) => string }> = ({ group, money }) => {
  const { t } = useTranslation('payroll');
  const [open, setOpen] = useState(true);
  const meta = CATEGORY_META[group.type];
  const negative = meta.sign === '-';

  return (
    <div className="rounded-xl border border-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-4 py-3"
      >
        <span className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: meta.color }} aria-hidden />
          <span className="text-sm font-semibold text-charcoal">{t(meta.labelKey)}</span>
          {group.type === 'EMPLOYER_CONTRIBUTION' && (
            <span className="text-[10px] text-muted-foreground">{t('myPay.notDeducted')}</span>
          )}
        </span>
        <span className="flex items-center gap-2">
          <span className={cn('text-sm font-semibold tabular-nums', negative ? 'text-[#991B1B]' : 'text-charcoal')}>
            {negative ? '−' : ''}{money(group.total)}
          </span>
          <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      {open && (
        <ul className="space-y-1 border-t border-border px-4 py-3">
          {group.items.map((c) => (
            <li key={c.code} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                {c.label}
                {c.explanation && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-muted-foreground/70 hover:text-charcoal" aria-label={t('myPay.explainAria', { item: c.label })}>
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">{c.explanation}</TooltipContent>
                  </Tooltip>
                )}
              </span>
              <span className={cn('tabular-nums', negative ? 'text-[#991B1B]' : 'text-charcoal')}>
                {negative ? '−' : '+'}{money(c.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Drop zero-amount lines (e.g. a skipped HESLB/SDL) and now-empty groups. */
export function pruneCategories(categories: CategoryGroup[]): CategoryGroup[] {
  return categories
    .map((g) => ({ ...g, items: g.items.filter((i) => i.amount !== 0) }))
    .filter((g) => g.items.length > 0);
}
